import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, KeyRound, Trash2, ShieldCheck, User, Send, MailCheck, AlertTriangle, Zap, CircleCheck, CircleDot } from "lucide-react";
import { roleLabel } from "@/lib/constants";

const ROLES = ["Admin", "Advisor", "Execution", "ServiceRep"];

/**
 * /app/team — Admin-only page to create, edit, deactivate, and reset passwords
 * for team members (Advisor / Execution / ServiceRep / additional Admins).
 * The last Admin cannot be deleted (server-enforced too).
 */
export default function TeamMembers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [pwDlg, setPwDlg] = useState(null); // holds the target user for password reset
  const [f, setF] = useState({ name: "", email: "", role: "Advisor" });
  const [pw, setPw] = useState("");

  const load = async () => {
    const { data } = await api.get("/users");
    setUsers(data.sort((a, b) => a.role.localeCompare(b.role) || a.name.localeCompare(b.name)));
  };
  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!f.name || !f.email) { toast.error("Name and email required"); return; }
    try {
      const { data } = await api.post("/users", f);  // password omitted → invite flow
      toast.success(`Invite emailed to ${data.email}`);
      setDlg(false);
      setF({ name: "", email: "", role: "Advisor" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to invite"); }
  };

  const resendInvite = async (u) => {
    try {
      await api.post(`/users/${u.id}/resend-invite`);
      toast.success(`Fresh invite sent to ${u.email}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      toast.success(`${u.name} ${u.active ? "deactivated" : "reactivated"}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`${u.name} removed`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const [wipeDlg, setWipeDlg] = useState(false);
  const [wipeConfirm, setWipeConfirm] = useState("");
  const [wipeBusy, setWipeBusy] = useState(false);
  const [wipeResult, setWipeResult] = useState(null);
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    api.get("/admin/config-status").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  const wipeAll = async () => {
    setWipeBusy(true);
    try {
      const { data } = await api.post("/admin/wipe-test-data?confirm=YES");
      setWipeResult(data.wiped);
      toast.success("Test data wiped clean");
      // Reload config-status counts too
      api.get("/admin/config-status").then(({ data }) => setCfg(data)).catch(() => {});
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setWipeBusy(false); }
  };

  const resetPw = async () => {
    if (pw.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    try {
      await api.post(`/users/${pwDlg.id}/reset-password`, { new_password: pw });
      toast.success(`Password reset for ${pwDlg.name}`);
      setPwDlg(null);
      setPw("");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="overline text-primary">Admin</p>
          <h1 className="font-dashboard text-3xl font-bold tracking-tight">Team members</h1>
          <p className="mt-1 text-sm text-muted-foreground">Invite advisors, execution and service reps — they set their own password from the emailed link.</p>
        </div>
        <Button onClick={() => setDlg(true)} className="gap-1" data-testid="add-user-btn">
          <Plus className="h-4 w-4" /> Invite team member
        </Button>
      </div>

      {cfg && (
        <div className="grid gap-4 md:grid-cols-2">
          <Card className="border-border bg-white p-5" data-testid="config-status-card">
            <div className="mb-3 flex items-center gap-2">
              <Zap className="h-4 w-4 text-accent" />
              <h3 className="font-dashboard text-sm font-bold">Integrations</h3>
            </div>
            <ul className="space-y-1.5 text-sm">
              {Object.entries(cfg.integrations).map(([key, v]) => (
                <li key={key} className="flex items-center justify-between" data-testid={`integ-${key}`}>
                  <span className="text-muted-foreground">{v.label}</span>
                  {v.live
                    ? <span className="inline-flex items-center gap-1 rounded-full bg-accent/10 px-2 py-0.5 text-[11px] font-medium text-accent"><CircleCheck className="h-3 w-3" /> Live</span>
                    : <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium text-muted-foreground"><CircleDot className="h-3 w-3" /> Mocked</span>}
                </li>
              ))}
            </ul>
            <div className="mt-3 border-t border-border pt-3 text-[11px] text-muted-foreground">
              <div>Sender: <b>{cfg.integrations.resend_email.sender || "—"}</b></div>
              <div>App URL: <b>{cfg.environment.public_app_url || "—"}</b></div>
            </div>
          </Card>

          <Card className="border-border bg-white p-5" data-testid="wipe-panel">
            <div className="mb-3 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-primary" />
              <h3 className="font-dashboard text-sm font-bold">Database</h3>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center text-sm">
              <div><div className="tabular-nums text-2xl font-bold">{cfg.db_counts.clients}</div><div className="text-[11px] text-muted-foreground">clients</div></div>
              <div><div className="tabular-nums text-2xl font-bold">{cfg.db_counts.prospects}</div><div className="text-[11px] text-muted-foreground">prospects</div></div>
              <div><div className="tabular-nums text-2xl font-bold">{cfg.db_counts.plans}</div><div className="text-[11px] text-muted-foreground">plans</div></div>
            </div>
            <Button
              variant="outline"
              className="mt-4 w-full gap-1 border-primary/40 text-primary hover:bg-primary/5"
              onClick={() => { setWipeConfirm(""); setWipeResult(null); setWipeDlg(true); }}
              data-testid="wipe-open-btn"
            >
              <Trash2 className="h-4 w-4" /> Wipe test data
            </Button>
            <p className="mt-2 text-[11px] text-muted-foreground">
              Removes every prospect, client, plan, appointment, review and Intelligence upload — but keeps team members intact.
            </p>
          </Card>
        </div>
      )}

      <Card className="border-border bg-white p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40">
            <tr className="text-left text-xs uppercase text-muted-foreground">
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 font-medium flex items-center gap-2">
                  {u.role === "Admin" ? <ShieldCheck className="h-4 w-4 text-primary" /> : <User className="h-4 w-4 text-muted-foreground" />}
                  {u.name}
                  {u.id === me?.id && <span className="ml-1 rounded bg-secondary px-1.5 py-0.5 text-[10px] text-muted-foreground">you</span>}
                </td>
                <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                <td className="px-4 py-3">{roleLabel(u.role)}</td>
                <td className="px-4 py-3">
                  {u.status === "invited"
                    ? <span className="inline-flex items-center gap-1 text-accent"><MailCheck className="h-3 w-3" /> Invited</span>
                    : u.active
                      ? <span className="text-accent">Active</span>
                      : <span className="text-muted-foreground">Inactive</span>}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {u.id !== me?.id && (
                    <>
                      {u.status === "invited" && (
                        <Button size="sm" variant="ghost" onClick={() => resendInvite(u)} data-testid={`resend-invite-${u.id}`} title="Resend invite">
                          <Send className="h-3 w-3" />
                        </Button>
                      )}
                      {u.status !== "invited" && (
                        <Button size="sm" variant="ghost" onClick={() => setPwDlg(u)} data-testid={`reset-pw-${u.id}`} title="Reset password">
                          <KeyRound className="h-3 w-3" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => toggleActive(u)} data-testid={`toggle-active-${u.id}`}>
                        {u.active ? "Deactivate" : "Reactivate"}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(u)} data-testid={`delete-user-${u.id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle>Invite team member</DialogTitle></DialogHeader>
          <div className="space-y-3 py-3">
            <p className="text-xs text-muted-foreground">
              They&apos;ll receive a branded email with a set-password link — no need to share credentials manually. The link is valid for 48 hours.
            </p>
            <div><Label>Full name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="new-user-name" /></div>
            <div><Label>Email</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="new-user-email" /></div>
            <div>
              <Label>Role</Label>
              <select className="mt-2 h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="new-user-role">
                {ROLES.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
              </select>
            </div>
          </div>
          <DialogFooter><Button onClick={invite} className="gap-1" data-testid="save-new-user"><Send className="h-4 w-4" /> Send invite</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={wipeDlg} onOpenChange={(v) => { if (!wipeBusy) setWipeDlg(v); }}>
        <DialogContent data-testid="wipe-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-primary" /> Wipe test data</DialogTitle></DialogHeader>
          {!wipeResult ? (
            <div className="space-y-3 py-3">
              <p className="text-sm text-muted-foreground">
                This permanently deletes every prospect, client, plan, appointment, review, magic link, notification and Intelligence upload from the database.
                Staff logins and the seeded reference data are preserved.
              </p>
              <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-xs">
                <div className="mb-1 font-medium text-primary">Currently in the database</div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div><div className="text-lg font-bold tabular-nums">{cfg?.db_counts.clients ?? 0}</div><div className="text-[10px] text-muted-foreground">clients</div></div>
                  <div><div className="text-lg font-bold tabular-nums">{cfg?.db_counts.prospects ?? 0}</div><div className="text-[10px] text-muted-foreground">prospects</div></div>
                  <div><div className="text-lg font-bold tabular-nums">{cfg?.db_counts.plans ?? 0}</div><div className="text-[10px] text-muted-foreground">plans</div></div>
                </div>
              </div>
              <div>
                <Label>Type <b>WIPE</b> to confirm</Label>
                <Input value={wipeConfirm} onChange={(e) => setWipeConfirm(e.target.value)} placeholder="WIPE" data-testid="wipe-confirm-input" />
              </div>
            </div>
          ) : (
            <div className="space-y-2 py-3 text-sm">
              <p className="text-accent">Wipe complete. Summary:</p>
              <div className="max-h-56 overflow-y-auto rounded-md border border-border bg-secondary/40 p-3 text-xs">
                <ul className="space-y-1">
                  {Object.entries(wipeResult).map(([k, v]) => (
                    <li key={k} className="flex justify-between"><span className="text-muted-foreground">{k}</span><b className="tabular-nums">{v}</b></li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          <DialogFooter>
            {!wipeResult ? (
              <Button
                variant="destructive"
                disabled={wipeConfirm !== "WIPE" || wipeBusy}
                onClick={wipeAll}
                data-testid="wipe-confirm-btn"
                className="gap-1"
              >
                <Trash2 className="h-4 w-4" /> {wipeBusy ? "Wiping…" : "Wipe now"}
              </Button>
            ) : (
              <Button onClick={() => { setWipeDlg(false); setWipeResult(null); setWipeConfirm(""); }} data-testid="wipe-close-btn">Close</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwDlg} onOpenChange={(v) => { if (!v) { setPwDlg(null); setPw(""); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset password for {pwDlg?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-3">
            <p className="text-sm text-muted-foreground">Type a new password below, then share it with them securely.</p>
            <div>
              <Label>New password</Label>
              <Input type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" data-testid="reset-pw-input" />
            </div>
          </div>
          <DialogFooter><Button onClick={resetPw} data-testid="reset-pw-save">Reset password</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
